"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { Button, Portal } from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { Toast, ToastProps } from "@/_components/ui/Toast/Toast";

interface ToastItem extends ToastProps {
  id: string;
}

interface ToastContextType {
  addToast: (toast: Omit<ToastItem, "id">) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let globalToasterInstance: ToastContextType | null = null;

const InternalToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((toast: Omit<ToastItem, "id">) => {
    const id = uuidv4();
    const newToast: ToastItem = {
      ...toast,
      id,
    };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      return updated.slice(-3);
    });

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = React.useMemo(
    () => ({ addToast, removeToast, clearAllToasts }),
    [addToast, removeToast, clearAllToasts],
  );

  React.useEffect(() => {
    globalToasterInstance = contextValue;
    return () => {
      globalToasterInstance = null;
    };
  }, [contextValue]);

  return (
    <Portal>
      <ToastContext.Provider value={contextValue}>
        {children}
        <div
          style={{
            position: "fixed",
            bottom: "16px",
            right: "16px",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              {...toast}
              onClose={() => removeToast(toast.id)}
              placement="bottom-right"
            />
          ))}
        </div>
      </ToastContext.Provider>
    </Portal>
  );
};

const useToaster = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToaster must be used within a ToastProvider");
  }
  return context;
};

const getToasterInstance = () => {
  if (!globalToasterInstance) {
    throw new Error(
      "Toaster not initialized. Make sure <Toaster /> is rendered in your app.",
    );
  }
  return globalToasterInstance;
};
export const toaster = {
  success: (options: {
    title?: string;
    description?: string;
    duration?: number;
  }) => {
    const { addToast } = getToasterInstance();
    return addToast({
      title: options.title,
      description: options.description,
      color: "success",
      timeout: options.duration || 4000,
    });
  },

  error: (options: {
    title?: string;
    description?: string;
    duration?: number;
  }) => {
    const { addToast } = getToasterInstance();
    return addToast({
      title: options.title,
      description: options.description,
      color: "danger",
      timeout: options.duration || 6000,
    });
  },

  warning: (options: {
    title?: string;
    description?: string;
    duration?: number;
  }) => {
    const { addToast } = getToasterInstance();
    return addToast({
      title: options.title,
      description: options.description,
      color: "warning",
      timeout: options.duration || 5000,
    });
  },

  info: (options: {
    title?: string;
    description?: string;
    duration?: number;
  }) => {
    const { addToast } = getToasterInstance();
    return addToast({
      title: options.title,
      description: options.description,
      color: "primary",
      timeout: options.duration || 4000,
    });
  },

  loading: (options: { title?: string; description?: string }) => {
    const { addToast } = getToasterInstance();
    return addToast({
      title: options.title,
      description: options.description,
      color: "primary",
      timeout: 0,
      promise: new Promise(() => {}),
    });
  },

  promise: function <T>(
    promise: Promise<T>,
    options: {
      loading?: { title?: string; description?: string };
      success?: { title?: string; description?: string };
      error?: { title?: string; description?: string };
    },
  ) {
    const { addToast, removeToast } = getToasterInstance();
    const toastId = addToast({
      title: options.loading?.title || "Loading...",
      description: options.loading?.description,
      color: "primary",
      timeout: 0,
      promise: promise.then(
        () => {
          removeToast(toastId);
          addToast({
            title: options.success?.title || "Success",
            description: options.success?.description,
            color: "success",
            timeout: 4000,
          });
        },
        () => {
          removeToast(toastId);
          addToast({
            title: options.error?.title || "Error",
            description: options.error?.description,
            color: "danger",
            timeout: 6000,
          });
        },
      ),
    });
    return toastId;
  },

  create: (options: {
    title?: string;
    description?: string;
    color?:
      | "default"
      | "primary"
      | "secondary"
      | "success"
      | "warning"
      | "danger";
    duration?: number;
    action?: { label: string; onClick: () => void };
    closable?: boolean;
  }) => {
    const { addToast } = getToasterInstance();
    return addToast({
      title: options.title,
      description: options.description,
      color: options.color || "default",
      timeout: options.duration || 4000,
      hideCloseButton: !options.closable,
      endContent: options.action ? (
        <Button onClick={options.action.onClick}>{options.action.label}</Button>
      ) : undefined,
    });
  },

  dismiss: (id: string) => {
    const { removeToast } = getToasterInstance();
    removeToast(id);
  },

  pause: () => {
    console.warn("pause() is not supported in the new Toast component");
  },

  resume: () => {
    console.warn("resume() is not supported in the new Toast component");
  },
};

export const Toaster = () => {
  return <InternalToastProvider>{null}</InternalToastProvider>;
};

export const useToast = useToaster;
